import React, { useEffect, useRef, useState } from 'react';
import { find, uniq } from 'lodash';

import { DangerAlert, HighlightAlert } from '@magoosh/lib/alerts';
import { FontAwesomeIcon, MagooshAIIcon } from '@magoosh/lib/icons';
import useAuthenticityToken from '@magoosh/utils/use_authenticity_token';
import { paths } from 'config/path_helpers';

import * as api from '@test_prep/app/api';
import ContextualLink from '@test_prep/app/components/contextual_link';

import { ScoreReport } from '../types';

import colors from '@magoosh/lib/styles/colors.module.scss';

interface Props {
  mockTestId: number;
  scoreReports: ScoreReport[];
}

const ERROR = 'error';
const FINISHED = 'finished';
const PROCESSING = 'processing';
const WAITING = 'waiting';

const ScoreReportsStatus: React.FC<Props> = (props) => {
  const initialStatus = () => {
    if (props.scoreReports.every((scoreReport) => scoreReport.status === 'waiting')) {
      return WAITING;
    } else if (props.scoreReports.some((scoreReport) => scoreReport.status === 'error')) {
      return ERROR;
    } else if (props.scoreReports.some((scoreReport) => scoreReport.status === 'processing')) {
      return PROCESSING;
    } else if (props.scoreReports.every((scoreReport) => scoreReport.status === 'finished')) {
      return FINISHED;
    } else {
      // Might be a combo of waiting/finished
      return PROCESSING;
    }
  };

  const poller = useRef(null);
  const [status, setStatus] = useState<string>(initialStatus());

  useEffect(() => {
    if (status === 'error' || status === 'finished') return;

    pollForStatusUpdates();

    return () => clearInterval(poller.current)
  }, []);

  const pollForStatusUpdates = () => {
    if (poller.current) clearInterval(poller.current);

    poller.current = setInterval(() => {
      api.fetchMockTestsGradingRequestsStatus(props.mockTestId).then((gradingRequests) => {
        setStatus(gradingRequests.status);

        if (gradingRequests.status === 'finished') {
          clearInterval(poller.current);
          window.location.reload();
        } else if (gradingRequests.status === 'error') {
          clearInterval(poller.current);
        }
      });
    }, 5000);
  };

  const renderReportTypesFragment = (linkColor) => {
    return (
      <>
        {
          props.scoreReports.map((scoreReport, idx) => (
            <>
              {(() => {
                if (idx !== 0 && props.scoreReports.length > 2) {
                  if (idx === props.scoreReports.length - 1) {
                    return <>, and </>;
                  } else {
                    return <>, </>;
                  }
                } else if (idx !== 0 && props.scoreReports.length === 2) {
                  return <> and </>;
                }
              })()}

              <ContextualLink
                href={paths.answerScoreReport(scoreReport.answerId)}
                style={{ color: linkColor }}
              >
                {scoreReport.reportType}
              </ContextualLink>
            </>
          ))
        }
      </>
    );
  };

  const retryGrading = () => {
    api.regradeMockTestsGradingRequests(props.mockTestId).then((gradingRequests) => {
      setStatus(gradingRequests.status);
      pollForStatusUpdates();
    });
  };

  if (status === ERROR) {
    return (
      <DangerAlert>
        <div className="align-items-start align-items-lg-center d-flex flex-column flex-lg-row gap-s">
          <>
            <div>
              We ran into an issue grading your {renderReportTypesFragment(colors.brandDanger)} {props.scoreReports.length === 1 ? 'answer' : 'answers'}.
            </div>

            <div className="d-flex flex-1 justify-content-end">
              <a className="small text-danger" onClick={retryGrading}>
                Retry Grading
              </a>
            </div>
          </>
        </div>
      </DangerAlert>
    );
  } else {
    return (
      <HighlightAlert>
        <div className="align-items-start align-items-lg-center d-flex flex-column flex-lg-row gap-s">
          <>
            <div className="hidden-sm hidden-xs">
              <MagooshAIIcon />
            </div>

            <div>
              {
                status === FINISHED ? (
                  <>
                    We've finished grading your {renderReportTypesFragment(colors.purple)} {props.scoreReports.length === 1 ? 'answer' : 'answers'}.
                  </>
                ) : (
                  <>
                    We're grading your {renderReportTypesFragment(colors.purple)} {props.scoreReports.length === 1 ? 'answer' : 'answers'}. This can take a minute or two.
                  </>
                )
              }
            </div>

            {
              status !== FINISHED && (
                <div className="d-flex flex-1 justify-content-end">
                  <div className="hidden-xs">
                    <FontAwesomeIcon icon="spinner" className="fa-pulse" />
                  </div>
                </div>
              )
            }
          </>
        </div>
      </HighlightAlert>
    );
  }
};

export default ScoreReportsStatus;
